import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface TokenResponse {
  id_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
}

/**
 * Service managing the OAuth2 BFF authorization code flow.
 * Keeps all sensitive credentials on the backend; the frontend only handles
 * the code exchange and token storage in memory.
 *
 * @author oauth2-bff
 */
@Injectable({ providedIn: 'root' })
export class OAuth2Service {

  private readonly SESSION_STATE_KEY = 'oauth2_state';
  private readonly SESSION_EXCHANGE_COMPLETED_KEY = 'oauth2_exchange_completed';
  private readonly SESSION_EXCHANGE_FAILED_KEY = 'oauth2_exchange_failed';
  private readonly HEX_BYTE_PAD = -2;

  private tokens: TokenResponse | null = null;

  constructor(private readonly http: HttpClient) {}

  // -------------------------------------------------------------------------
  // Flow initiation
  // -------------------------------------------------------------------------

  /**
   * Generates a secure state, stores it in sessionStorage, then redirects
   * the browser to the backend BFF /start endpoint to begin the OAuth2 flow.
   */
  initiateAuthorizationFlow(): void {
    const state = this.generateSecureState();
    sessionStorage.setItem(this.SESSION_STATE_KEY, state);

    // Clear any stale exchange flags before starting a new flow
    sessionStorage.removeItem(this.SESSION_EXCHANGE_COMPLETED_KEY);
    sessionStorage.removeItem(this.SESSION_EXCHANGE_FAILED_KEY);

    const startUrl = this.buildBffUrl(environment.oauth2Config.startUrl) + `?state=${state}`;
    console.info('[OAuth2] Initiating authorization flow via BFF start endpoint:', startUrl);
    this.navigateTo(startUrl);
  }

  // -------------------------------------------------------------------------
  // Callback handling
  // -------------------------------------------------------------------------

  /**
   * Validates the state parameter and POSTs the code to the backend BFF /exchange endpoint.
   * Treats only HTTP 200 as success.
   */
  handleCallback(code: string, state: string): Observable<boolean> {
    const storedState = sessionStorage.getItem(this.SESSION_STATE_KEY);
    if (!storedState || storedState !== state) {
      console.info('[OAuth2] State mismatch — possible CSRF. Stored:', storedState, 'Received:', state);
      return of(false);
    }

    console.info('[OAuth2] Callback received, proxying code to backend BFF');

    const exchangeUrl = this.buildBffUrl(environment.oauth2Config.exchangeUrl);
    return this.http.post(exchangeUrl, { code, state }, { observe: 'response', responseType: 'text' }).pipe(
      map((response: HttpResponse<string>) => {
        if (response.status === 200) {
          console.info('[OAuth2] Code exchange successful');
          sessionStorage.setItem(this.SESSION_EXCHANGE_COMPLETED_KEY, 'true');
          sessionStorage.removeItem(this.SESSION_STATE_KEY);
          sessionStorage.removeItem(this.SESSION_EXCHANGE_FAILED_KEY);
          return true;
        }
        console.info('[OAuth2] Code exchange returned non-200 status:', response.status);
        return false;
      }),
      catchError((error) => {
        console.info('[OAuth2] Code exchange HTTP error:', error);
        return of(false);
      })
    );
  }

  // -------------------------------------------------------------------------
  // Token refresh
  // -------------------------------------------------------------------------

  /**
   * Posts the current refresh_token to the backend BFF /refresh endpoint
   * and updates the in-memory id_token.
   */
  refreshIdToken(): Observable<TokenResponse> {
    const refreshUrl = this.buildBffUrl(environment.oauth2Config.refreshUrl);
    console.info('[OAuth2] Refreshing id_token via BFF refresh endpoint');
    return this.http.post<TokenResponse>(refreshUrl, { refreshToken: this.tokens?.refresh_token }).pipe(
      tap((response: TokenResponse) => {
        console.info('[OAuth2] Token refresh successful — new id_token received');
        if (this.tokens) {
          this.tokens.id_token = response.id_token;
          if (response.expires_in) {
            this.tokens.expires_in = response.expires_in;
          }
        }
      })
    );
  }

  // -------------------------------------------------------------------------
  // Backend logout notification
  // -------------------------------------------------------------------------

  /**
   * Notifies the backend to delete the OAuth2 session token from DynamoDB.
   * Fire-and-forget — errors are logged but do not block the local logout flow.
   */
  notifyBackendLogout(): Observable<void> {
    const logoutUrl = this.buildBffUrl(environment.oauth2Config.logoutUrl);
    console.info('[OAuth2] Notifying backend of logout:', logoutUrl);
    return this.http.post<void>(logoutUrl, {}, { withCredentials: true }).pipe(
      tap(() => console.info('[OAuth2] Backend logout notification sent successfully')),
      catchError((error) => {
        console.info('[OAuth2] Backend logout notification failed (non-blocking):', error);
        return of(undefined as void);
      })
    );
  }

  // -------------------------------------------------------------------------
  // State accessors
  // -------------------------------------------------------------------------

  getStoredTokens(): TokenResponse | null {
    return this.tokens;
  }

  isExchangeCompleted(): boolean {
    return sessionStorage.getItem(this.SESSION_EXCHANGE_COMPLETED_KEY) === 'true';
  }

  markExchangeFailed(): void {
    sessionStorage.setItem(this.SESSION_EXCHANGE_FAILED_KEY, 'true');
  }

  hasExchangeFailed(): boolean {
    return sessionStorage.getItem(this.SESSION_EXCHANGE_FAILED_KEY) === 'true';
  }

  clearExchangeFailed(): void {
    sessionStorage.removeItem(this.SESSION_EXCHANGE_FAILED_KEY);
  }

  clearTokens(): void {
    this.tokens = null;
    sessionStorage.removeItem(this.SESSION_STATE_KEY);
    sessionStorage.removeItem(this.SESSION_EXCHANGE_COMPLETED_KEY);
  }

  decodeToken(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(payload) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  getTokenExpiration(token: string): number {
    const decoded = this.decodeToken(token);
    if (!decoded || typeof decoded['exp'] !== 'number') {
      return 0;
    }
    return decoded['exp'] as number;
  }

  // -------------------------------------------------------------------------
  // Protected helpers (overridable in tests)
  // -------------------------------------------------------------------------

  protected navigateTo(url: string): void {
    window.location.href = url;
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private generateSecureState(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array)
      .map(b => b.toString(16).padStart(Math.abs(this.HEX_BYTE_PAD), '0'))
      .join('');
  }

  private buildBffUrl(path: string): string {
    const baseUrl: string =
      (environment as Record<string, unknown> & { config?: { domain?: string } })?.config?.domain
      ?? window.location.origin;

    // Handle paths prefixed with a port specifier, e.g. ':8010/enclave-base-security-service/...'
    const portPrefixMatch = path.match(/^:(\d+)\/(.*)/);
    if (portPrefixMatch) {
      const port = portPrefixMatch[1];
      const actualPath = portPrefixMatch[2];
      const baseWithoutPort = baseUrl.replace(/:\d+$/, '').replace(/\/+$/, '');
      return `${baseWithoutPort}:${port}/${actualPath}`;
    }

    const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${normalizedBase}${normalizedPath}`;
  }
}
